import { isValidPhoneNumber } from "react-phone-number-input";
import { z } from "zod";
import type { FormMode } from "@/pages/AdminPortal/Therapist/Upsert";
import type { Therapist } from "@/types/admin-portal/therapist";
import {
	EMPLOYMENT_STATUSES,
	EMPLOYMENT_TYPES,
	GENDERS,
	RELIGIONS,
} from "./constants";

/**
 * Resolves a message key from the `therapists` namespace. Passed in rather than imported
 * so this module stays free of the i18n singleton, which reads `window` on import and
 * would drag a browser global into every test that touches this file.
 */
export type TranslateFn = (key: string) => string;

/**
 * Returns the CSS class for the badge variant based on the therapist's employment status.
 *
 * @param data - The employment status of the therapist.
 * @returns The CSS class for the badge variant.
 */
export const getEmpStatusBadgeVariant = (
	data: Therapist["employmentStatus"][number],
) => {
	if (data === "HOLD") return "bg-amber-500 hover:bg-amber-500/75";

	if (data === "INACTIVE") return "bg-rose-500 hover:bg-rose-500/75";

	return "bg-emerald-500 hover:bg-emerald-500/75";
};

/**
 * CSS classes for the Tags column badges.
 *
 * Tinted rather than solid on purpose: solid saturated badges are the Status column's
 * language in this table (see getEmpStatusBadgeVariant above), and a Tag wearing the same
 * weight reads as a second status rather than as a piece of work to do. Sitting a tinted
 * badge next to the Activity column's "Suspended" keeps the two distinguishable.
 *
 * One axis, two hues: rose for a record that is already wrong, amber for one that is
 * heading there or still incomplete.
 */
export const TAG_BADGE_VARIANTS = {
	alreadyWrong:
		"text-rose-800 bg-rose-100 hover:bg-rose-200 dark:bg-rose-900 dark:text-rose-100 dark:hover:bg-rose-800",
	notYetLate:
		"text-amber-800 bg-amber-100 hover:bg-amber-200 dark:bg-amber-900 dark:text-amber-100 dark:hover:bg-amber-800",
} as const;

/**
 * Generates a Zod schema for validating therapist form data based on the provided mode.
 *
 * @param mode - The form mode, either "create" or "update".
 *
 * @returns A Zod schema for validating therapist form data.
 *
 * The schema includes the following fields:
 * - `user`: An object containing:
 *   - `email`: A required string that must be a valid email address.
 *   - `password`: A required string that must be between 8 and 64 characters long, and contain at least one uppercase letter, one number, and one symbol.
 *   - `passwordConfirmation`: A required string that must match the password and follow the same validation rules.
 * - `name`: A required string with a minimum length of 3 characters.
 * - `batch`: A nullable number with a minimum value of 1.
 * - `phoneNumber`: A required string that must be a valid phone number.
 * - `gender`: A nullable enum value from `GENDERS`.
 * - `employmentStatus`: A nullable enum value from `EMPLOYMENT_STATUSES`.
 * - `employmentType`: A nullable enum value from `EMPLOYMENT_TYPES`.
 * - `modalities`: An array of strings, possibly empty.
 * - `specializations`: An array of strings, possibly empty.
 * - `bankDetails`: A non-empty array of objects containing:
 *   - `bankName`: A required string.
 *   - `accountNumber`: A required string.
 *   - `accountHolderName`: A required string.
 *   - `active`: A boolean with a default value of false.
 * - `service`: An object containing:
 *   - `id`: A required number.
 *   - `name`: A required string.
 *   - `code`: A required string.
 * - `addresses`: A non-empty array of objects containing:
 *   - `id`: An optional string or number.
 *   - `country`: A required string.
 *   - `countryCode`: A required string.
 *   - `state`: A required string.
 *   - `city`: A required string.
 *   - `postalCode`: A required string.
 *   - `address`: A required string.
 *   - `active`: A boolean with a default value of false.
 *
 * The schema also includes the following refinements:
 * - Ensures that the password and password confirmation match.
 * - In "create" mode, ensures that email, password, and password confirmation are provided.
 */
/**
 * Nearly every stored registration number is "<service code>-<digits>", which lets the form
 * show the prefix as a fixed affix and have the admin type digits only. These two decide when
 * that affix can represent a value; anything else is entered whole, since ADR 0003 forbids
 * rejecting a number just because the operations spreadsheet shapes it differently.
 */
const STANDARD_REGISTRATION_NUMBER = /^([A-Za-z0-9]+)-(\d+)$/;

export function isStandardRegistrationNumber(value: string) {
	return STANDARD_REGISTRATION_NUMBER.test(value);
}

/**
 * The digits an affixed field should show, or undefined when the value has a shape the affix
 * cannot express. Bare digits count: on the create form they are typed before a service is
 * picked, and the prefix is applied once one is.
 */
export function registrationNumberDigits(value: string) {
	return (
		STANDARD_REGISTRATION_NUMBER.exec(value)?.[2] ??
		(/^\d+$/.test(value) ? value : undefined)
	);
}

export function nikPayload(nik: string | null): { nik?: string | null } {
	return nik === null || nik.trim() !== "" ? { nik } : {};
}

export const getFormSchema = (
	mode: FormMode,
	t: TranslateFn,
	{ addressesRequired = true } = {},
) => {
	const user =
		mode === "create"
			? z
					.object({
						email: z
							.string()
							.email()
							.min(1, { message: t("validation.email_required") }),
						password: z
							.string()
							.min(8, t("validation.password_min"))
							.max(64, t("validation.password_max"))
							.regex(/[A-Z]/, t("validation.password_uppercase"))
							.regex(/\d/, t("validation.password_number"))
							.regex(/[\W_]/, t("validation.password_symbol")),
						passwordConfirmation: z
							.string()
							.min(8, t("validation.password_confirmation_min"))
							.max(64, t("validation.password_confirmation_max"))
							.regex(/[A-Z]/, t("validation.password_confirmation_uppercase"))
							.regex(/\d/, t("validation.password_confirmation_number"))
							.regex(/[\W_]/, t("validation.password_confirmation_symbol")),
					})
					.refine((data) => data.password === data.passwordConfirmation, {
						message: t("validation.password_mismatch"),
						path: ["passwordConfirmation"],
					})
			: z.any();
	const TherapistSchema = z.object({
		user,
		name: z.string().min(3, { message: t("validation.name_required") }),
		batch: z.coerce
			.number({ message: t("validation.batch_invalid") })
			.min(1, { message: t("validation.batch_required") })
			.nullable(),
		// no format validation on purpose: these numbers come from the operations
		// spreadsheet, whose prefixes are not reliably our service codes and whose shapes
		// are mixed, so any regex would reject valid data. See docs/adr/0003.
		registrationNumber: z
			.string()
			.trim()
			.min(1, { message: t("validation.registration_required") }),
		phoneNumber: z
			.string()
			.min(1, { message: t("validation.phone_required") })
			.refine(isValidPhoneNumber, { message: t("validation.phone_invalid") }),
		telegramId: z
			.string()
			.regex(/^@[a-zA-Z0-9_]{5,32}$/, {
				message: t("validation.telegram_invalid"),
			})
			.or(z.literal(""))
			.optional(),
		nik: z
			.string()
			.refine(
				(value) =>
					value.trim() === "" || /^\d{16}$/.test(value.replace(/[ .\t-]/g, "")),
				{ message: t("validation.nik_invalid") },
			)
			.nullable(),
		gender: z.enum(GENDERS).nullable(),
		religion: z.enum(RELIGIONS).nullable(),
		employmentStatus: z.enum(EMPLOYMENT_STATUSES).nullable(),
		employmentType: z.enum(EMPLOYMENT_TYPES).nullable(),
		contractStartDate: z.string().nullable().optional(),
		contractEndDate: z.string().nullable().optional(),
		// not required: a profession with no list for the field (Perawat has no
		// specializations) would otherwise force the admin to invent free text
		modalities: z.array(z.string()),
		specializations: z.array(z.string()),
		bankDetails: z
			.array(
				z.object({
					id: z.union([z.string(), z.number()]).optional(),
					bankName: z
						.string()
						.min(1, { message: t("validation.bank_name_required") }),
					accountNumber: z
						.string()
						.min(1, { message: t("validation.account_number_required") }),
					accountHolderName: z
						.string()
						.min(1, { message: t("validation.holder_required") }),
					active: z.boolean().default(false),
				}),
			)
			.nonempty(t("validation.bank_required")),
		service: z.object({
			id: z.number(),
			name: z
				.string()
				.min(1, { message: t("validation.service_name_required") }),
			code: z
				.string()
				.min(1, { message: t("validation.service_code_required") }),
		}),
		addresses: z
			.array(
				z.object({
					id: z.union([z.string(), z.number()]).optional(),
					country: z
						.string()
						.min(1, { message: t("validation.country_required") }),
					countryCode: z
						.string()
						.min(1, { message: t("validation.country_code_required") }),
					state: z.string().min(1, { message: t("validation.state_required") }),
					city: z.string().min(1, { message: t("validation.city_required") }),
					postalCode: z
						.string()
						.nullable()
						.transform((val) => val || undefined)
						.optional(),
					address: z
						.string()
						.min(1, { message: t("validation.address_required") }),
					active: z.boolean().default(false),
					lat: z.coerce
						.number({ message: t("validation.lat_invalid") })
						.optional()
						.nullable(),
					lng: z.coerce
						.number({ message: t("validation.lng_invalid") })
						.optional()
						.nullable(),
					coordinates: z.string().optional(),
				}),
			)
			.min(addressesRequired ? 1 : 0, t("validation.address_list_required")),
	});

	return TherapistSchema;
};
export type TherapistFormSchema = z.infer<ReturnType<typeof getFormSchema>>;
